// ──────────────────────────────────────────────────────────────────────────
// IL QUADERNO
//
// Le lezioni e gli esercizi che il maestro si tiene da parte per riusarli.
// Una lezione del quaderno è un programma intero — obiettivo, tipo di
// giocatore, su cosa si lavora, le quattro fasi coi loro esercizi — come
// quello di una lezione (src/utils/lessonContent.utils.js). Un esercizio è
// uno solo, salvato da solo: di una fase precisa ("fase tecnica") oppure
// generico, buono per qualsiasi fase.
//
// Usare qualcosa del quaderno lo COPIA nella lezione: cambiare il quaderno
// dopo non cambia le lezioni già fatte. La lezione si ricorda solo da dove
// viene, per dirlo ("Dal quaderno: «Rovescio lungolinea»") e per contare
// quante volte una voce è stata usata e quando l'ultima.
//
// Una voce può avere più **varianti**, una per livello (#169): la stessa
// lezione per l'Orange e per il 3.0, ognuna col suo programma e una riga che
// dice cosa cambia. Il nome (e la fase, per un esercizio) è della voce, il
// resto della variante.
//
// Il quaderno è di chi lo scrive, che lo legge e lo cambia da qualsiasi
// circolo; una voce si può **condividere coi colleghi** di un circolo, che
// la vedono e la usano ma non la cambiano (firestore.rules, `notebook`).
// ──────────────────────────────────────────────────────────────────────────
import { DateTime } from "luxon"
import {
	EXERCISE_MAX_TEXT,
	FOCUS_AREAS,
	PHASES,
	chosenAreas,
	isAreaOn,
	normalizeLessonContent,
	playerStyleLabel,
} from "./lessonContent.utils"
import { normalizeSchema, schemaSignature } from "./exerciseSchema.utils"
import { PTR_LEVELS, SAT_LEVELS, getPtrLevel, getSatLevel } from "./func.utils"

export const NOTEBOOK_LESSON = "lesson"
export const NOTEBOOK_EXERCISE = "exercise"

// I limiti sono anche in firestore.rules (`isValidNotebookEntry`): chi
// cambia uno dei due cambia anche l'altro. Il testo di un esercizio è
// quello di un esercizio della lezione.
export const NOTEBOOK_TITLE_MAX = 120
export const NOTEBOOK_TEXT_MAX = EXERCISE_MAX_TEXT
// la riga che dice cosa cambia una variante rispetto alle altre
export const NOTEBOOK_NOTE_MAX = 200

// ── I livelli ──
// Una variante è per un livello del SAT (la palla: Red, Orange, Green,
// Yellow) o della scala PTR del CAD (1.0 – 5.0), oppure per nessuno: le voci
// di prima delle varianti non ce l'hanno. Si scrive come si legge: "orange",
// "3.0". Sono al massimo tanti quanti i livelli, più una senza.
export const NOTEBOOK_LEVELS = [
	...SAT_LEVELS.map(level => ({ id: level.value, label: level.label, color: level.color })),
	...PTR_LEVELS.map(level => ({ id: level.label, label: level.label, color: null })),
]
export const NOTEBOOK_MAX_VARIANTS = NOTEBOOK_LEVELS.length + 1

export const getNotebookLevel = levelID =>
	NOTEBOOK_LEVELS.find(level => level.id === levelID) ?? null

// nell'ordine dei livelli, quella senza livello per prima
const levelRank = levelID => {
	const i = NOTEBOOK_LEVELS.findIndex(level => level.id === levelID)
	return i < 0 ? -1 : i
}

export const levelLabel = levelID => getNotebookLevel(levelID)?.label ?? "Senza livello"

// Il livello di una lezione, come lo dice il quaderno: quello del SAT o il
// PTR del CAD, se è stato segnato
export const lessonNotebookLevel = lessonData =>
	getSatLevel(lessonData?.satLevel)?.value ?? getPtrLevel(lessonData?.ptrLevel)?.label ?? null

// Una variante nuova ha un id suo, che le lezioni si ricordano. Quelle di
// prima delle varianti sono "main".
export const MAIN_VARIANT = "main"
export const newVariantID = () => Math.random().toString(36).slice(2, 10)

const PHASE_IDS = PHASES.map(phase => phase.id)

export const phaseLabel = phaseID => PHASES.find(phase => phase.id === phaseID)?.label ?? ""

// Un esercizio generico va bene per qualsiasi fase
export const exerciseFitsPhase = (entry, phaseID) =>
	entry?.kind === NOTEBOOK_EXERCISE && (!entry.phase || entry.phase === phaseID)

export const exercisePhaseLabel = phase => (phase ? phaseLabel(phase) : "Qualsiasi fase")

// Una variante com'è arrivata: il livello (o nessuno), cosa cambia e, per
// una lezione il programma, per un esercizio il testo e lo schema
const normalizeVariant = (kind, raw, fallbackID) => ({
	variantID: typeof raw?.variantID === "string" && raw.variantID ? raw.variantID : fallbackID,
	level: getNotebookLevel(raw?.level) ? raw.level : null,
	note: typeof raw?.note === "string" ? raw.note.slice(0, NOTEBOOK_NOTE_MAX) : "",
	content: kind === NOTEBOOK_LESSON ? notebookLessonContent(raw?.content) : null,
	text: kind === NOTEBOOK_EXERCISE && typeof raw?.text === "string" ? raw.text : "",
	schema: kind === NOTEBOOK_EXERCISE ? normalizeSchema(raw?.schema) : null,
})

// Le varianti nell'ordine dei livelli. Due con lo stesso livello o lo
// stesso id non ci dovrebbero essere: resta la prima.
export const sortVariants = variants => {
	const seenIDs = new Set()
	const seenLevels = new Set()
	return variants
		.filter(variant => {
			const levelKey = variant.level ?? ""
			if (seenIDs.has(variant.variantID) || seenLevels.has(levelKey)) return false
			seenIDs.add(variant.variantID)
			seenLevels.add(levelKey)
			return true
		})
		.map((variant, i) => ({ variant, i }))
		.sort((a, b) => levelRank(a.variant.level) - levelRank(b.variant.level) || a.i - b.i)
		.map(({ variant }) => variant)
}

// Quello che di una variante si vede, sulla voce
const variantFields = variant => ({
	variantID: variant.variantID,
	level: variant.level,
	note: variant.note,
	content: variant.content,
	text: variant.text,
	schema: variant.schema,
})

// Il quaderno com'è arrivato da Firestore, senza sorprese: un esercizio di
// una fase che non c'è più diventa generico.
//
// Su Firestore la prima variante sta al primo livello del documento, come
// prima che ci fossero le varianti, e le altre in `variants`: così una voce
// di prima è una voce con una variante sola, e l'app di prima legge ancora
// la sua. Qui le varianti stanno tutte in `variants`, in ordine di livello,
// e la voce mostra la prima; `entryAt` la mostra con un'altra.
export const normalizeNotebookEntry = (entryID, raw) => {
	const data = raw && typeof raw === "object" ? raw : {}
	const kind = data.kind === NOTEBOOK_EXERCISE ? NOTEBOOK_EXERCISE : NOTEBOOK_LESSON
	const variants = sortVariants([
		normalizeVariant(kind, data, MAIN_VARIANT),
		...(Array.isArray(data.variants) ? data.variants : [])
			.slice(0, NOTEBOOK_MAX_VARIANTS)
			.map((variant, i) => normalizeVariant(kind, variant, `v${i + 1}`)),
	])
	return {
		entryID,
		coachID: data.coachID ?? "",
		kind,
		title: typeof data.title === "string" ? data.title : "",
		phase: kind === NOTEBOOK_EXERCISE && PHASE_IDS.includes(data.phase) ? data.phase : null,
		variants,
		...variantFields(variants[0]),
		sharedSchoolIDs: Array.isArray(data.sharedSchoolIDs)
			? data.sharedSchoolIDs.filter(id => typeof id === "string")
			: [],
		usesCount: Number.isFinite(data.usesCount) ? data.usesCount : 0,
		schoolUses: Number.isFinite(data.schoolUses) ? data.schoolUses : 0,
		lastUsedAt: data.lastUsedAt ?? null,
		copiedFrom:
			typeof data.copiedFrom?.coachName === "string"
				? { coachID: data.copiedFrom.coachID ?? "", coachName: data.copiedFrom.coachName }
				: null,
		createdAt: data.createdAt ?? null,
		updatedAt: data.updatedAt ?? null,
	}
}

// Una variante della voce, per id; `null` se non c'è (più)
export const variantOf = (entry, variantID) =>
	(entry?.variants ?? []).find(variant => variant.variantID === (variantID || MAIN_VARIANT)) ??
	null

// La voce vista con una delle sue varianti: il programma, il testo, lo
// schema e il livello sono quelli. Una variante che non c'è lascia la prima.
export const entryAt = (entry, variantID) => {
	const variant = variantOf(entry, variantID)
	return variant ? { ...entry, ...variantFields(variant) } : entry
}

// I livelli che hanno già una variante, tranne quella che si sta cambiando
export const usedLevels = (entry, exceptVariantID = null) =>
	(entry?.variants ?? [])
		.filter(variant => variant.variantID !== exceptVariantID)
		.map(variant => variant.level)

// Le varianti con una cambiata, una nuova o una in meno: si salva la voce
// intera, con tutte le sue varianti
export const withVariant = (entry, variantID, fields) =>
	entry.variants.map(variant =>
		variant.variantID === variantID ? { ...variant, ...fields } : variant,
	)

export const withNewVariant = (entry, fields) => [
	...entry.variants,
	{
		variantID: newVariantID(),
		level: null,
		note: "",
		content: null,
		text: "",
		schema: null,
		...fields,
	},
]

export const withoutVariant = (entry, variantID) =>
	entry.variants.filter(variant => variant.variantID !== variantID)

// ── Di chi è ──
// Le voci arrivano dal provider già segnate: `isMine` se sono di chi
// guarda, e di un collega `authorName`. Condivisa vuol dire condivisa nel
// circolo che si sta guardando.
export const isMine = entry => entry?.isMine !== false

export const isSharedIn = (entry, schoolID) =>
	Boolean(schoolID) && (entry?.sharedSchoolIDs ?? []).includes(schoolID)

export const NOTEBOOK_OWNERS = [
	{ id: "all", label: "Tutte", short: "Tutte" },
	{ id: "mine", label: "Mie", short: "Solo le mie" },
	{ id: "colleagues", label: "Dei colleghi", short: "Dei colleghi" },
]

export const filterByOwner = (entries, owner) =>
	(entries ?? []).filter(entry =>
		owner === "mine" ? isMine(entry) : owner === "colleagues" ? !isMine(entry) : true,
	)

const toMillis = timestamp =>
	timestamp?.toMillis ? timestamp.toMillis() : timestamp instanceof Date ? timestamp.getTime() : 0

const lastActivity = entry =>
	Math.max(toMillis(entry.lastUsedAt), toMillis(entry.updatedAt), toMillis(entry.createdAt))

// Senza accenti e maiuscole: "volee" trova "Volée"
const fold = text =>
	String(text ?? "")
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()

// Le parole in cui una voce si fa trovare: il nome, chi l'ha scritta, e di
// ogni variante il livello, cosa cambia, quello che c'è scritto e, per una
// lezione, il tipo di giocatore e su cosa si lavora
const variantText = (entry, variant) => {
	const level = variant.level ? levelLabel(variant.level) : ""
	if (entry.kind === NOTEBOOK_EXERCISE) return [level, variant.note, variant.text].join(" ")
	const content = normalizeLessonContent(variant.content)
	return [
		level,
		variant.note,
		content.objective,
		playerStyleLabel(content.playerStyle),
		...chosenAreas(content).flatMap(({ area, chosen }) => [
			area.label,
			...chosen.map(item => item.label),
		]),
		...Object.values(content.phases).flatMap(phase => [phase.objective, ...phase.exercises]),
	].join(" ")
}

const searchableText = entry =>
	[
		entry.title,
		entry.authorName ?? "",
		entry.kind === NOTEBOOK_EXERCISE ? exercisePhaseLabel(entry.phase) : "",
		...(entry.variants ?? [entry]).map(variant => variantText(entry, variant)),
	].join(" ")

// Le voci di un tipo che rispondono alla ricerca, dalla più usata di
// recente: quello che si usa di più sta in cima. Ogni parola cercata deve
// esserci, in qualsiasi ordine.
export const searchNotebook = (entries, kind, search = "") => {
	const words = fold(search).split(/\s+/).filter(Boolean)
	return (entries ?? [])
		.filter(entry => entry.kind === kind)
		.filter(entry => {
			if (words.length === 0) return true
			const text = fold(searchableText(entry))
			return words.every(word => text.includes(word))
		})
		.sort((a, b) => lastActivity(b) - lastActivity(a) || a.title.localeCompare(b.title, "it"))
}

// "gio 15 ott", il giorno dell'ultima volta; vuoto se non c'è
const lastUseDay = entry => {
	const last = toMillis(entry.lastUsedAt)
	return last ? DateTime.fromMillis(last).setLocale("it").toFormat("ccc d LLL") : ""
}

// Una lezione è usata, un esercizio usato
const neverUsed = entry => (entry.kind === NOTEBOOK_EXERCISE ? "Mai usato" : "Mai usata")

const timesLabel = count => (count === 1 ? "1 volta" : `${count} volte`)
const used = entry => (entry.kind === NOTEBOOK_EXERCISE ? "Usato" : "Usata")

// "Usata 6 volte": quante, senza quando. Gli usi dei colleghi si contano a
// parte (`schoolUses`): a chi l'ha scritta dicono quanto serve al circolo,
// "Usata 6 volte, 8 dai colleghi". A un collega si dice il totale, "Usata
// 14 volte nel circolo".
export const usesLabel = entry => {
	const mine = entry.usesCount ?? 0
	const others = entry.schoolUses ?? 0
	if (!isMine(entry)) {
		const all = mine + others
		return all > 0 ? `${used(entry)} ${timesLabel(all)} nel circolo` : neverUsed(entry)
	}
	if (mine <= 0 && others <= 0) return neverUsed(entry)
	if (others <= 0) return `${used(entry)} ${timesLabel(mine)}`
	if (mine <= 0) return `${used(entry)} ${timesLabel(others)} dai colleghi`
	return `${used(entry)} ${timesLabel(mine)} da te, ${others} dai colleghi`
}

// "Ultima volta gio 15 ott": quando, senza quante. È quello che si guarda
// scorrendo il quaderno — l'ho appena fatta, o è ora di riprenderla — e
// quante volte lo dice la voce aperta. Di un collega, che quando l'ha
// usata lui conta poco, si dice quante volte nel circolo.
export const lastUseLabel = entry => {
	if (!isMine(entry)) return usesLabel(entry)
	if ((entry.usesCount ?? 0) <= 0) return neverUsed(entry)
	const day = lastUseDay(entry)
	return day ? `Ultima volta ${day}` : usesLabel(entry)
}

// "Usata 6 volte, l'ultima gio 15 ott": tutt'e due, dove una voce si
// sceglie in una riga sola (il quaderno dentro il programma)
export const usageLabel = entry => {
	if (!isMine(entry)) return usesLabel(entry)
	if ((entry.usesCount ?? 0) <= 0) return neverUsed(entry)
	const day = lastUseDay(entry)
	return `${used(entry)} ${timesLabel(entry.usesCount)}${day ? `, l'ultima ${day}` : ""}`
}

// "Giulia F.": il nome di un collega dove ci sta poco (la copertina)
export const shortName = name => {
	const words = (name ?? "").split(/\s+/).filter(Boolean)
	if (words.length < 2) return words[0] ?? ""
	return `${words[0]} ${words[words.length - 1][0]}.`
}

// Di chi è, in poche parole: "condivisa", "solo tua", o il nome del collega
export const ownerLabel = (entry, schoolID) => {
	const exercise = entry.kind === NOTEBOOK_EXERCISE
	if (!isMine(entry)) return entry.authorName ? `di ${entry.authorName}` : "di un collega"
	if (isSharedIn(entry, schoolID)) return exercise ? "condiviso" : "condivisa"
	return exercise ? "solo tuo" : "solo tua"
}

// ── I filtri del quaderno ──
// Sopra le voci, delle pastiglie restringono a quello che si cerca: le
// lezioni per su cosa si lavora (le macroaree del programma), gli esercizi
// per fase. Ci sono solo quelle che hanno almeno una voce: una pastiglia
// che non trova niente è una promessa vuota.

export const NOTEBOOK_ALL = "all"
// gli esercizi buoni per tutte le fasi
export const NOTEBOOK_ANY_PHASE = "anyPhase"

// Un esercizio di una fase la passa; uno generico passa tutte le fasi, come
// quando lo si prende dal programma (`exerciseFitsPhase`), e ha anche la sua
// pastiglia
const passesFilter = (entry, filterID) => {
	if (filterID === NOTEBOOK_ALL) return true
	if (entry.kind === NOTEBOOK_EXERCISE)
		return filterID === NOTEBOOK_ANY_PHASE ? !entry.phase : exerciseFitsPhase(entry, filterID)
	const area = FOCUS_AREAS.find(area => area.id === filterID)
	return Boolean(area) && isAreaOn(normalizeLessonContent(entry.content), area)
}

// Le pastiglie per un tipo di voce, nell'ordine del programma, ognuna
// `{ id, label }`; la prima è sempre "Tutte" o "Tutti"
export const notebookFilters = (entries, kind) => {
	const ofKind = (entries ?? []).filter(entry => entry.kind === kind)
	const options =
		kind === NOTEBOOK_EXERCISE
			? [
					...PHASES.filter(phase => ofKind.some(entry => entry.phase === phase.id)),
					...(ofKind.some(entry => !entry.phase)
						? [{ id: NOTEBOOK_ANY_PHASE, label: exercisePhaseLabel(null) }]
						: []),
				]
			: FOCUS_AREAS.filter(area => ofKind.some(entry => passesFilter(entry, area.id)))
	return [
		{ id: NOTEBOOK_ALL, label: kind === NOTEBOOK_EXERCISE ? "Tutti" : "Tutte" },
		...options.map(({ id, label }) => ({ id, label })),
	]
}

// I livelli che hanno almeno una variante tra queste voci, nell'ordine dei
// livelli: sulla stessa riga delle pastiglie, prima di loro
export const notebookLevelFilters = (entries, kind) => {
	const present = new Set(
		(entries ?? [])
			.filter(entry => entry.kind === kind)
			.flatMap(entry => (entry.variants ?? []).map(variant => variant.level))
			.filter(Boolean),
	)
	return NOTEBOOK_LEVELS.filter(level => present.has(level.id))
}

// Le voci che passano la pastiglia scelta e, se c'è, il livello: una voce
// che ha la variante di quel livello si mostra con quella, così quello che
// si legge (e le macroaree) sono di quel livello
export const filterNotebook = (entries, filterID, levelID = null) =>
	(entries ?? [])
		.map(entry => {
			if (!levelID) return entry
			const variant = (entry.variants ?? []).find(variant => variant.level === levelID)
			return variant ? entryAt(entry, variant.variantID) : null
		})
		.filter(entry => entry && passesFilter(entry, filterID))

// Un esercizio preso dal quaderno e poi cambiato lo dice: il testo o lo
// schema non sono più quelli che erano nel quaderno quando lo si è preso.
export const exerciseChanged = (source, text, schema = null) =>
	Boolean(source) &&
	((source.text ?? "").trim() !== (text ?? "").trim() ||
		(source.schemaKey ?? "") !== schemaSignature(schema))

// Le provenienze di una fase, una per esercizio: le fasi scritte a mano non
// le hanno salvate
export const phaseSources = phase =>
	(phase?.exercises ?? []).map((_, i) => phase?.sources?.[i] ?? null)

// Gli schemi di una fase, uno per esercizio, `null` dove non c'è
export const phaseSchemas = phase =>
	(phase?.exercises ?? []).map((_, i) => phase?.schemas?.[i] ?? null)

// La provenienza di un esercizio del quaderno: la voce com'è adesso, con
// la variante che si è presa
const exerciseSource = entry => {
	const schemaKey = schemaSignature(entry.schema)
	return {
		entryID: entry.entryID,
		title: entry.title,
		text: entry.text,
		...(schemaKey ? { schemaKey } : {}),
		...(entry.variantID ? { variantID: entry.variantID } : {}),
	}
}

// Il programma che una lezione del quaderno si tiene: quello della lezione,
// con gli esercizi presi dal quaderno, ma senza dire da quale altra lezione
// del quaderno veniva. Quella è storia della lezione, non della voce nuova.
// E senza com'è andata: il diario e le righe sugli allievi sono di quel
// giorno e di quegli allievi.
export function notebookLessonContent(content) {
	const { fromNotebook, diary, playerNotes, ...rest } = normalizeLessonContent(content)
	return rest
}

// Il programma di una lezione del quaderno, pronto da copiare in una
// lezione: dice da dove viene, e porta con sé gli esercizi presi dal
// quaderno che aveva dentro.
export const lessonContentFromNotebook = entry => ({
	...notebookLessonContent(entry.content),
	fromNotebook: lessonSource(entry),
})

// Da quale lezione del quaderno si parte, e da quale sua variante
export const lessonSource = entry => ({
	entryID: entry.entryID,
	title: entry.title,
	...(entry.variantID ? { variantID: entry.variantID } : {}),
})

// Una lezione del quaderno presa per una lezione che ha già le sue note di
// fine lezione: il programma lo sostituisce, le note restano
export const withNotebookLesson = (content, entry) => {
	const { diary, playerNotes } = normalizeLessonContent(content)
	return {
		...lessonContentFromNotebook(entry),
		...(diary ? { diary } : {}),
		...(playerNotes ? { playerNotes } : {}),
	}
}

// Un esercizio del quaderno in una fase: il testo e lo schema copiati in
// fondo — al posto della riga vuota, se l'ultima lo è — e l'esercizio da
// cui viene, com'era in quel momento.
export const withExercise = (content, phaseID, entry) => {
	const phase = content.phases[phaseID]
	const exercises = [...phase.exercises]
	const sources = phaseSources(phase)
	const schemas = phaseSchemas(phase)
	const last = exercises.length - 1
	if (last >= 0 && exercises[last].trim() === "" && !schemas[last]) {
		exercises.pop()
		sources.pop()
		schemas.pop()
	}
	return {
		...content,
		phases: {
			...content.phases,
			[phaseID]: {
				...phase,
				exercises: [...exercises, entry.text],
				sources: [...sources, exerciseSource(entry)],
				schemas: [...schemas, normalizeSchema(entry.schema)],
			},
		},
	}
}

// Un esercizio salvato nel quaderno da una lezione: da lì in poi viene dal
// quaderno, e com'è adesso è "già nel quaderno"
export const withSavedExercise = (content, phaseID, index, entry) => {
	const phase = content.phases[phaseID]
	const sources = phaseSources(phase)
	sources[index] = exerciseSource(entry)
	return { ...content, phases: { ...content.phases, [phaseID]: { ...phase, sources } } }
}

// ── Il quaderno andato avanti ──
// Un esercizio preso dal quaderno si copia, e il quaderno può cambiare dopo:
// gli si disegna lo schema, se ne corregge il testo. La lezione resta com'è,
// ma se lo si è fatto nel modo nuovo lo si deve poter riprendere. La
// provenienza tiene com'era l'esercizio quando lo si è preso: se la sua
// variante, nel quaderno, adesso è diversa da quella, il quaderno è andato
// avanti. Se è già uguale alla riga della lezione, non c'è niente da
// riprendere.

// Com'è adesso nel quaderno l'esercizio da cui viene questa riga, se è
// cambiato da quando lo si è preso: la voce vista con la sua variante, e
// cosa è cambiato. `null` se non è cambiato, o se la voce o la variante
// non ci sono più.
export const notebookUpdate = (source, text, schema, getEntry) => {
	if (!source?.entryID) return null
	const stored = getEntry(source.entryID)
	const variant = variantOf(stored, source.variantID)
	if (stored?.kind !== NOTEBOOK_EXERCISE || !variant) return null
	const entry = entryAt(stored, variant.variantID)
	if (!exerciseChanged(source, entry.text, entry.schema)) return null
	// la riga è già com'è il quaderno: è stata cambiata qui nello stesso modo
	if (!exerciseChanged(exerciseSource(entry), text, schema)) return null
	const before = source.schemaKey ?? ""
	const after = schemaSignature(entry.schema)
	return {
		entry,
		textChanged: (source.text ?? "").trim() !== (entry.text ?? "").trim(),
		schema: before === after ? null : !before ? "added" : !after ? "removed" : "changed",
		// cambiata anche qui: aggiornando, quello che si è cambiato si perde
		changedHere: exerciseChanged(source, text, schema),
	}
}

// Gli esercizi di un programma che nel quaderno sono andati avanti, nell'ordine
// delle fasi: `{ phaseID, index, ...notebookUpdate }`
export const notebookUpdates = (content, getEntry) => {
	const normalized = normalizeLessonContent(content)
	return PHASES.flatMap(phase => {
		const current = normalized.phases[phase.id]
		const sources = phaseSources(current)
		const schemas = phaseSchemas(current)
		return current.exercises.flatMap((text, index) => {
			const update = notebookUpdate(sources[index], text, schemas[index], getEntry)
			return update ? [{ phaseID: phase.id, index, ...update }] : []
		})
	})
}

// Cosa è cambiato nel quaderno, in una riga: "Nel quaderno ora c'è lo schema"
export const notebookUpdateLabel = ({ textChanged, schema }) => {
	if (textChanged && schema) return "Nel quaderno sono cambiati testo e schema"
	if (textChanged) return "Nel quaderno il testo è cambiato"
	if (schema === "added") return "Nel quaderno ora c'è lo schema"
	if (schema === "removed") return "Nel quaderno lo schema non c'è più"
	return "Nel quaderno lo schema è cambiato"
}

// La riga ripresa dal quaderno: il testo e lo schema di adesso, e da lì in
// poi la provenienza è questa. Non è un uso nuovo: l'esercizio era già
// nella lezione.
export const withNotebookUpdate = (content, phaseID, index, entry) => {
	const phase = content.phases[phaseID]
	const exercises = [...phase.exercises]
	const sources = phaseSources(phase)
	const schemas = phaseSchemas(phase)
	exercises[index] = entry.text ?? ""
	sources[index] = exerciseSource(entry)
	schemas[index] = normalizeSchema(entry.schema)
	return {
		...content,
		phases: { ...content.phases, [phaseID]: { ...phase, exercises, sources, schemas } },
	}
}

// Le voci del quaderno usate davvero in questo salvataggio: prese mentre il
// foglio era aperto e ancora lì quando si salva. Prenderne una e cambiare
// idea non la conta.
export const usedEntryIDs = (pickedIDs, content) => {
	const present = new Set([
		content?.fromNotebook?.entryID,
		...Object.values(content?.phases ?? {}).flatMap(phase =>
			(phase?.sources ?? []).map(source => source?.entryID),
		),
	])
	return [...new Set(pickedIDs)].filter(entryID => entryID && present.has(entryID))
}

// L'ultima volta è la data della lezione, non quella in cui la si è
// scritta: una lezione di ieri segnata oggi non è più recente di quella di
// domani preparata ieri.
export const laterUse = (lastUsedAt, lessonStart) =>
	toMillis(lessonStart) > toMillis(lastUsedAt) ? lessonStart : lastUsedAt

// Il nome che si propone salvando una lezione nel quaderno: il suo
// obiettivo, se c'è, se no su cosa si lavora
export const suggestedLessonTitle = content => {
	const normalized = normalizeLessonContent(content)
	if (normalized.objective) return normalized.objective.slice(0, NOTEBOOK_TITLE_MAX)
	return chosenAreas(normalized)
		.map(({ area, detail }) => detail || area.label)
		.join(", ")
		.slice(0, NOTEBOOK_TITLE_MAX)
}

// Il nome che si propone salvando un esercizio: il suo inizio, fino a una
// parola intera
export const suggestedExerciseTitle = text => {
	const clean = (text ?? "").trim().replace(/\s+/g, " ")
	if (clean.length <= 40) return clean
	const cut = clean.slice(0, 40)
	return cut.slice(0, cut.lastIndexOf(" ") > 20 ? cut.lastIndexOf(" ") : 40)
}

// ── Salvare una lezione cambiata ──
// Una lezione partita dal quaderno e poi cambiata, salvando chiede cosa
// fare: tenere le modifiche solo lì, aggiornare la variante da cui è
// partita, o farne una variante per un altro livello. Per gli esercizi presi
// dal quaderno e cambiati vale lo stesso. Di un collega non si cambia
// niente: o solo lì, o una copia nel proprio quaderno.

export const CHANGE_ONLY_HERE = "onlyHere"
export const CHANGE_UPDATE = "update"
export const CHANGE_NEW_VARIANT = "newVariant"
export const CHANGE_COPY = "copy"

// Il programma senza da dove vengono gli esercizi: salvare un esercizio nel
// quaderno cambia la provenienza, non quello che si fa
const programKey = content => {
	const { phases, ...rest } = notebookLessonContent(content)
	return JSON.stringify({
		...rest,
		phases: Object.fromEntries(
			Object.entries(phases).map(([phaseID, { sources, ...phase }]) => [phaseID, phase]),
		),
	})
}

// Quello che il salvataggio deve chiedere, nell'ordine del foglio: prima la
// lezione da cui si parte, poi gli esercizi. Ognuno con la voce (com'è
// adesso), la variante usata e, per un esercizio, dove sta. Una voce tolta,
// o di un collega che non la condivide più, non si può più cambiare: non
// chiede niente.
export const notebookChanges = (content, getEntry) => {
	const normalized = normalizeLessonContent(content)
	const changes = []
	const from = normalized.fromNotebook
	const lessonEntry = from ? getEntry(from.entryID) : null
	const lessonVariant = variantOf(lessonEntry, from?.variantID)
	if (lessonVariant && programKey(normalized) !== programKey(lessonVariant.content))
		changes.push({
			key: "lesson",
			kind: NOTEBOOK_LESSON,
			entry: entryAt(lessonEntry, lessonVariant.variantID),
		})
	PHASES.forEach(phase => {
		const { exercises } = normalized.phases[phase.id]
		const sources = phaseSources(normalized.phases[phase.id])
		const schemas = phaseSchemas(normalized.phases[phase.id])
		exercises.forEach((text, index) => {
			const source = sources[index]
			const entry = source ? getEntry(source.entryID) : null
			const variant = variantOf(entry, source?.variantID)
			if (!variant || !exerciseChanged(source, text, schemas[index])) return
			changes.push({
				key: `${phase.id}-${index}`,
				kind: NOTEBOOK_EXERCISE,
				entry: entryAt(entry, variant.variantID),
				phaseID: phase.id,
				index,
			})
		})
	})
	return changes
}

// Quello che la variante diventa con le modifiche di questa lezione
export const changedFields = (change, content) => {
	const normalized = normalizeLessonContent(content)
	if (change.kind === NOTEBOOK_LESSON) return { content: notebookLessonContent(normalized) }
	const phase = normalized.phases[change.phaseID]
	return {
		text: phase.exercises[change.index] ?? "",
		schema: phaseSchemas(phase)[change.index] ?? null,
	}
}

// La lezione, dopo: quello che è stato salvato nel quaderno viene da lì, com'è
// adesso. `saved` è la voce (o la copia) vista con la variante scritta.
export const withChangeSaved = (content, change, saved) =>
	change.kind === NOTEBOOK_LESSON
		? { ...content, fromNotebook: lessonSource(saved) }
		: withSavedExercise(content, change.phaseID, change.index, saved)

// La copia di una voce di un collega, nel proprio quaderno: tutte le sue
// varianti, quella usata com'è in questa lezione. Non è condivisa, e si
// ricorda di chi era.
export const copyOfEntry = (entry, variantID, fields, title) => ({
	kind: entry.kind,
	title: title ?? entry.title,
	phase: entry.phase,
	variants: withVariant(entry, variantID, fields),
	copiedFrom: { coachID: entry.coachID, coachName: entry.authorName ?? "" },
})
